import InfoIcon from "@mui/icons-material/Info";
import { Box, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../api/hooks/useCheckUserPermission";
import useColors from "../../../api/hooks/useColors";
import { assetKeys, scanKeys } from "../../../api/queryKeys";
import LoadingIcon from "../../../components/LoadingIcon";
import AppPage from "../../../components/structure/AppPage";
import { useFileNaming } from "../../../hooks/useFileNaming";
import {
  ComplexEntityTypes,
  ManualSteps,
  PageDisplayMode,
  PLATFORM_USER_ACTIONS,
  QueryParam,
} from "../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../utils/timestamp";
import { useEntities } from "../environment/hooks/useEntities";
import AssetsGrid from "./AssetsGrid";
import AssetSlider from "./AssetSlider";
import AssetsOverview from "./components/AssetsOverview";
import EntitiesInAssetTable from "./components/EntitiesInAssetTable/EntitiesInAssetTable";
import CreateNewAssetModal from "./components/wizard/CreateNewAssetModal";

const pageStates = {
  REGULAR: "regular",
  CREATION_MODAL_OPEN: "creation_modal_open",
  ASSET_DRAWER_OPEN: "asset_drawer_open",
};

export default function AssetsView() {
  const gridRef = useGridApiRef();
  const [pageState, setPageState] = useState(pageStates.REGULAR);
  const {
    fetchRegularAssets,
    fetchSmartAssets,
    trackEvent,
    getLastSuccessfulScan,
  } = useAPI();
  const { generateFileName } = useFileNaming();
  const [searchParams, setSearchParams] = useSearchParams();
  // May be reintroduced in the future (for now we only have the table display mode)
  const [displayMode, setDisplayMode] = useState(PageDisplayMode.CARD);
  const [appliedFilters, setAppliedFilters] = useState({
    updated: false,
    active: false,
    searchText: "",
    searchDisplayName: "",
  });
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const { useQueriesForEntities, useQueriesByTypes } = useEntities();

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_ASSET
  );

  const { data: regularAssets, isLoading: regularAssetsAreLoading } = useQuery({
    queryKey: assetKeys.regularAssets(),
    queryFn: async () => {
      let assets = await fetchRegularAssets();
      return assets;
    },
    select: (response) => response.data,
  });

  const { data: smartAssets, isLoading: smartAssetsAreLoading } = useQuery({
    queryKey: assetKeys.smartAssets(),
    queryFn: async () => {
      let assets = await fetchSmartAssets();
      return assets;
    },
    select: (response) => response.data,
  });

  const { data: lastSuccessfulScan } = useQuery({
    queryKey: scanKeys.lastSuccessfulScan(),
    queryFn: async () => {
      let scan = await getLastSuccessfulScan();
      return scan;
    },
    select: (response) => response.data,
  });

  // Fetch entity options for the autocomplete
  const { entities: rawEntityOptions, entitiesAreLoading } = useQueriesByTypes({
    types: Object.values(ComplexEntityTypes),
  });

  const allEntitiesIds = useMemo(() => {
    return [
      ...(regularAssets?.map((asset) => asset.entity_ids) || []),
      ...(smartAssets?.map((asset) => asset.entity_ids) || []),
    ].flat();
  }, [regularAssets, smartAssets]);

  // Format entity options for the autocomplete
  const entityOptions = useMemo(() => {
    if (!rawEntityOptions) return [];

    return rawEntityOptions
      .filter((entity) => allEntitiesIds.includes(entity.entity_id))
      .map((entity) => ({
        value: entity.entity_id,
        label: entity.display_name || entity.name || entity.entity_id,
        type: entity.type,
      }));
  }, [rawEntityOptions, allEntitiesIds]);

  // Memoize the combined cards to prevent unnecessary recalculations + filters
  const allCards = useMemo(() => {
    let allAssets = [...(smartAssets || []), ...(regularAssets || [])];
    if (regularAssetsAreLoading || smartAssetsAreLoading) return [];
    if (appliedFilters.active) {
      allAssets = allAssets.filter((asset) => asset.is_active);
    }
    if (appliedFilters.updated) {
      allAssets = allAssets.filter(
        (asset) => asset?.new_entity_ids?.length > 0
      );
    }
    if (appliedFilters.searchText) {
      allAssets = allAssets.filter((asset) =>
        asset?.entity_ids?.some((id) =>
          id.toLowerCase().includes(appliedFilters.searchText.toLowerCase())
        )
      );
    }
    return allAssets;
  }, [
    smartAssets,
    regularAssets,
    appliedFilters,
    regularAssetsAreLoading,
    smartAssetsAreLoading,
  ]);

  // Memoize chosen asset to prevent unnecessary re-renders
  const chosenAsset = useMemo(() => {
    const assetId = searchParams.get(QueryParam.id);
    if (assetId) {
      return allCards.find((asset) => asset?.asset_id === assetId);
    }

    return null;
  }, [searchParams, regularAssets, smartAssets, appliedFilters.searchText]);

  const {
    entitiesAreLoading: fetchedEntitiesAreLoading,
    requestedEntities: entitiesInChosenAsset = [],
  } = useQueriesForEntities({
    entityIDs: chosenAsset?.entity_ids || [],
  });

  // Memoize loading state to prevent unnecessary re-renders
  const assetsIsLoading = useMemo(() => {
    return (
      regularAssetsAreLoading ||
      smartAssetsAreLoading ||
      fetchedEntitiesAreLoading
    );
  }, [
    regularAssetsAreLoading,
    smartAssetsAreLoading,
    fetchedEntitiesAreLoading,
  ]);

  // Calculate overview data for AssetsOverview component
  const overviewData = useMemo(() => {
    if (assetsIsLoading) return {};

    // Calculate average coverage
    const totalCoverage = allCards.reduce(
      (sum, asset) => sum + (asset?.coverage || 0),
      0
    );
    const avgCoverage =
      allCards.length > 0 ? totalCoverage / allCards.length : 0;

    // Count attack paths - this is a placeholder, replace with actual data
    const attackScenarios = allCards.reduce(
      (sum, asset) => sum + (asset?.scenario_sets_count || 0),
      0
    );
    const totalBusinessValue = allCards.reduce(
      (sum, asset) => sum + (asset?.business_value || 0),
      0
    );
    const valueAtRisk = totalBusinessValue * (1 - avgCoverage / 100) || 0;

    const potentialAttackScenarios = Math.round(
      attackScenarios * (1 - avgCoverage / 100)
    );
    const mitigatedAttackScenarios = Math.round(
      attackScenarios - potentialAttackScenarios
    );

    return {
      assets: allCards,
      coverage: avgCoverage,
      attack_scenarios: attackScenarios,
      potential_attack_scenarios: potentialAttackScenarios,
      mitigated_attack_scenarios: mitigatedAttackScenarios,
      total_business_value: totalBusinessValue,
      value_at_risk: valueAtRisk,
    };
  }, [smartAssets, regularAssets, assetsIsLoading]);

  const handleCreateAssetClick = () => {
    const message = `User opened the modal to create an asset, from the assets page`;
    trackEvent(message);
    setPageState(pageStates.CREATION_MODAL_OPEN);
  };

  useEffect(() => {
    if (appliedFilters.searchText) {
      const asset = allCards.find((asset) =>
        asset?.entity_ids?.some((id) =>
          id.toLowerCase().includes(appliedFilters.searchText.toLowerCase())
        )
      );
      if (asset) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set(QueryParam.id, asset.asset_id);
        setSearchParams(newParams);
        setDisplayMode(PageDisplayMode.TABLE);
      }
    }
  }, [appliedFilters.searchText]);

  const handleOnAssetCardClick = (asset) => {
    const newParams = new URLSearchParams(searchParams);
    const message = `User clicked on asset ${asset?.name} (${asset?.asset_id}), from the assets page`;
    if (asset) {
      newParams.set(QueryParam.id, asset.asset_id);
      trackEvent(message);
    } else {
      newParams.delete(QueryParam.id);
    }
    if (displayMode === PageDisplayMode.CARD) {
      setDisplayMode(PageDisplayMode.TABLE);
    }
    setSearchParams(newParams);
  };

  const handleExport = () => {
    const message =
      "User click on 'Export' button to download the asset entities table";
    trackEvent(message);
    const csvExportOptions = {
      fileName: generateFileName(`${chosenAsset?.name}_asset_entities`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  return (
    <AppPage title="Critical Assets">
      {assetsIsLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingIcon />
        </Box>
      ) : (
        <>
          {" "}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-end",
                mt: -4,
              }}
            >
              <InfoIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.primary_main,
                }}
              />
              <Typography variant="caption">
                Data as of{" "}
                {getPrettyDateTime(
                  lastSuccessfulScan?.timestamp_utc,
                  showLocalTime
                )}
                . Changes will update after the next scan.
              </Typography>
            </Box>
            <AssetsOverview
              setSearchParams={setSearchParams}
              overviewData={overviewData}
              handleCreateAssetClick={handleCreateAssetClick}
              fetchedEntitiesAreLoading={fetchedEntitiesAreLoading}
              hasPermission={hasPermission}
              displayMode={displayMode}
              setDisplayMode={setDisplayMode}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              entityOptions={entityOptions || []}
              entitiesAreLoading={entitiesAreLoading}
              allCards={allCards}
            />

            {displayMode === PageDisplayMode.TABLE ? (
              <AssetSlider
                allCard={allCards}
                chosenAsset={chosenAsset}
                handleOnAssetCardClick={handleOnAssetCardClick}
                entitiesInChosenAsset={entitiesInChosenAsset}
                hasPermission={hasPermission}
                displayMode={displayMode}
              />
            ) : (
              <AssetsGrid
                allCard={allCards}
                handleOnAssetCardClick={handleOnAssetCardClick}
                chosenAsset={chosenAsset}
                entitiesInChosenAsset={entitiesInChosenAsset}
                hasPermission={hasPermission}
                displayMode={displayMode}
              />
            )}
          </Box>
          {chosenAsset && displayMode === PageDisplayMode.TABLE && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 3,
              }}
            >
              <EntitiesInAssetTable
                gridRef={gridRef}
                entitiesInAsset={entitiesInChosenAsset}
                fetchedEntitiesAreLoading={fetchedEntitiesAreLoading}
                handleTableExport={handleExport}
                asset={chosenAsset}
                searchString={appliedFilters.searchDisplayName}
              />
            </Box>
          )}
          <CreateNewAssetModal
            steps={ManualSteps}
            open={pageState === pageStates.CREATION_MODAL_OPEN}
            onClose={() => {
              trackEvent(
                `User closed the modal to create an asset, from the assets page`
              );
              setPageState(pageStates.REGULAR);
            }}
            assetToEdit={null}
          />
        </>
      )}
    </AppPage>
  );
}
